/* eslint-disable react/jsx-no-useless-fragment */
import { memo, FC, useState, useEffect, useMemo } from 'react';
import Router from 'next/router';
import useScrollPosition from '@react-hook/window-scroll';
import clsx from 'clsx';
import type { PdpData, DatoProductImage } from '@framework/api/types';
import Head from 'next/head';
import { useTranslation as t } from '@utils/hooks';
import {
  SubscriptionProductCollection,
  SubscriptionProductUpgradeOption,
} from 'fragile/types';
import PurchaseOptionsNav from '@components/PDP/PurchaseOptionsNav';
import * as RegPdpStyles from '@components/PDP/styles';
import YotpoWidget from '@components/YotpoReviews/YotpoWidget';
import useFpsStore, { getSetFps, getClearFps } from '@stores/use-fps-store';
import PartsPerformanceCard from 'fragile/components/PartsPerformance/PartsPerformanceCard';
import ColorSelectItems from './ColorSelect/ColorSelectItems';
import ColorSelectGallery from './ColorSelectGallery/ColorSelectGallery';
import * as AvailabilityMessageStyles from '../../components/PDP/AvailabilityMessage/styles';
import PdpTierSelector from './PdpTierSelector/PdpTierSelector';
import { type GroupedTab } from './PdpTierSelector/PdpTierSelector';
import BuySummary from './BuySummary';
import PdpConditionSelector, {
  ConditionOption,
} from './PdpConditionSelector/PdpConditionSelector';

// Define the data structure type
type SubscriptionDataWithVersions = {
  [ConditionOption.LIKE_NEW]: SubscriptionProductCollection;
  [ConditionOption.GUARANTEED_NEW]: SubscriptionProductCollection;
};

type Props = {
  setUpgradeOption: (option: SubscriptionProductUpgradeOption) => void;
  selectedUpgradeOption: SubscriptionProductUpgradeOption;
  setConditionOption: (option: ConditionOption) => void;
  selectedConditionOption: ConditionOption;
  subscriptionProductName: string;
  shortDescription: string;
  images: DatoProductImage[];
  data?: PdpData;
  price: string;
  subscriptionProduct: SubscriptionProductCollection;
  allSubscriptionProducts: SubscriptionDataWithVersions[];
  isOutOfStockConditionMap: Record<ConditionOption, boolean>;
};

const SubscriptionPdp: FC<Props> = ({
  subscriptionProductName,
  shortDescription,
  images,
  price,
  subscriptionProduct,
  selectedUpgradeOption,
  allSubscriptionProducts,
  setUpgradeOption,
  selectedConditionOption,
  setConditionOption,
  isOutOfStockConditionMap,
}) => {
  const isRegionallyAvailable = true;

  const setFps = useFpsStore(getSetFps);
  const clearFps = useFpsStore(getClearFps);

  // Effect to update FPS data based on the selected upgrade option and condition
  useEffect(() => {
    Router.events.on('routeChangeComplete', () => clearFps());

    let timeout = null;

    timeout = setTimeout(() => {
      // Use FPS data from the currently selected upgrade option
      if (selectedUpgradeOption?.fromCms?.fps) {
        setFps(selectedUpgradeOption.fromCms.fps);
      } else {
        clearFps();
      }
    }, 2);

    return () => {
      Router.events.off('routeChangeComplete', () => clearFps());
      clearTimeout(timeout);
    };
  }, [clearFps, selectedUpgradeOption, selectedConditionOption, setFps]);

  const oosChipClass = clsx(
    AvailabilityMessageStyles.base,
    AvailabilityMessageStyles.outOfStock
  );

  const newChipClass = clsx(
    AvailabilityMessageStyles.base,
    AvailabilityMessageStyles.newItem
  );

  const [isVisible, setIsVisible] = useState(false);
  const scrollY = useScrollPosition();

  useEffect(() => {
    setIsVisible(scrollY > 500);
  }, [scrollY]);

  // Group the collections and filter out duplicates
  const groupedTabs: GroupedTab[] = useMemo(() => {
    // Extract collections and filter out duplicates
    const groupedCollections = allSubscriptionProducts
      .map(
        product =>
          product[ConditionOption.LIKE_NEW].fromCms.collection?.[0].name ??
          'Other'
      )
      .filter((value, index, self) => self.indexOf(value) === index);

    return groupedCollections
      .map(collection => ({
        title: collection,
        // Get lowest price product
        price: Math.floor(
          allSubscriptionProducts
            .filter(
              product =>
                product[ConditionOption.LIKE_NEW].fromCms.collection?.[0]
                  .name === collection
            )
            .sort((a, b) =>
              a[ConditionOption.LIKE_NEW].monthlyPrice >
              b[ConditionOption.LIKE_NEW].monthlyPrice
                ? 1
                : -1
            )[0][ConditionOption.LIKE_NEW].monthlyPrice / 100
        ),
        tabs: allSubscriptionProducts
          .filter(
            product =>
              product[ConditionOption.LIKE_NEW].fromCms.collection?.[0].name ===
              collection
          )
          .sort((a, b) =>
            a[ConditionOption.LIKE_NEW].monthlyPrice >
            b[ConditionOption.LIKE_NEW].monthlyPrice
              ? 1
              : -1
          )
          .map(product => ({
            title:
              product[ConditionOption.LIKE_NEW].fromCms.shortProductName ??
              product[ConditionOption.LIKE_NEW].fromCms.name,
            description:
              product[ConditionOption.LIKE_NEW].fromCms
                .customerProfileDescription,
            slug: product[ConditionOption.LIKE_NEW].fromCms.slug,
            price: `$${Math.floor(
              product[ConditionOption.LIKE_NEW].monthlyPrice / 100
            )}/mo`,
            popular: product[ConditionOption.LIKE_NEW].fromCms.isPopular,
            specs: product[ConditionOption.LIKE_NEW].fromCms.whyChooseBody
              ?.split('\n')
              ?.map(item => item.trim()),
          })),
      }))
      .sort((a, b) => (a.price > b.price ? 1 : -1));
  }, [allSubscriptionProducts]);

  const selectedPrimaryIndex: number = useMemo(
    () =>
      groupedTabs.findIndex(
        tab => tab.title === subscriptionProduct.fromCms.collection?.[0].name
      ),
    [groupedTabs, subscriptionProduct.fromCms.collection]
  );

  const selectedSecondaryIndex: number = useMemo(
    () =>
      groupedTabs[selectedPrimaryIndex]?.tabs.findIndex(
        tab => tab.slug === subscriptionProduct.fromCms.slug
      ),
    [groupedTabs, selectedPrimaryIndex, subscriptionProduct.fromCms.slug]
  );

  const parts = useMemo(
    () =>
      allSubscriptionProducts.find(
        product =>
          product[selectedConditionOption]?.fromCms?.slug ===
          subscriptionProduct.fromCms.slug
      )[selectedConditionOption].parts,
    [allSubscriptionProducts, selectedConditionOption, subscriptionProduct]
  );

  return (
    <div className={RegPdpStyles.detailsGrid}>
      <Head>
        {(images ?? []).map(item => (
          <link key={item.id} rel="preload" as="image" href={item.url} />
        ))}

        {subscriptionProduct.fromCms.subscriptionUpgrades
          .map(cmsUpgrade =>
            cmsUpgrade.upgradeOptions.map(opt => opt.images ?? [])
          )
          .flat(2)
          .map(item => (
            <link key={item.id} rel="preload" as="image" href={item.url} />
          ))}
      </Head>

      <div className={RegPdpStyles.column} data-test-id="product-column">
        {isOutOfStockConditionMap[selectedConditionOption] ? (
          <p
            className={oosChipClass}
            data-test-id="product-availability-message"
            suppressHydrationWarning
          >
            {t('message_out_of_stock')}
          </p>
        ) : (
          <p
            className={newChipClass}
            data-test-id="product-availability-message"
            suppressHydrationWarning
          >
            {t('message_new_item')}
          </p>
        )}
        <div
          className={RegPdpStyles.mainDetail}
          data-test-id="main-detail-block"
        >
          <div className={RegPdpStyles.fullSpanWrapper}>
            <h1
              className={RegPdpStyles.titleBase}
              data-test-id="product-name-heading"
            >
              {subscriptionProductName}
            </h1>
            {subscriptionProduct?.fromCms?.yotpoProductId && (
              <YotpoWidget
                type="star-rating"
                id={subscriptionProduct?.fromCms?.yotpoProductId}
                template="product"
              />
            )}
          </div>
          <p
            className={clsx(
              RegPdpStyles.greyText,
              RegPdpStyles.fullSpanWrapper
            )}
            data-test-id="product-shrt-des"
          >
            {shortDescription}
          </p>
          {isRegionallyAvailable && (
            <>
              <h2
                className={RegPdpStyles.titlePrice}
                data-test-id="product-price"
              >
                <span
                  suppressHydrationWarning
                  className={RegPdpStyles.isOnSale(false)}
                  data-test-id="product-price-amount"
                >
                  {price}
                </span>
              </h2>
            </>
          )}
        </div>
        <PurchaseOptionsNav
          slug={subscriptionProduct.fromCms.slug}
          siblingSlug={subscriptionProduct.fromCms.prebuildLink?.slug}
          regionalServices={subscriptionProduct.fromCms.regionalServices}
        />
        <PdpTierSelector
          groupedTabs={groupedTabs}
          selectedPrimaryIndex={selectedPrimaryIndex}
          selectedSecondaryIndex={selectedSecondaryIndex}
        />
        {subscriptionProduct.products[0].upgrades
          .map(productUpgrade =>
            productUpgrade.fromCms.isColorUpgrade ? (
              <ColorSelectItems
                key={productUpgrade.id}
                selectedUpgradeOption={selectedUpgradeOption}
                setUpgradeOption={setUpgradeOption}
                upgradeOptions={productUpgrade.options.sort(
                  (a, b) => a.displayOrder - b.displayOrder
                )}
              />
            ) : null
          )
          .filter(item => item !== null)}
        <PdpConditionSelector
          selectedConditionOption={selectedConditionOption}
          setConditionOption={setConditionOption}
          selectedUpgradeOption={selectedUpgradeOption}
        />
        <div className="lg:hidden py-4">
          <PartsPerformanceCard
            selectedConditionOption={selectedConditionOption}
            selectedUpgradeOption={selectedUpgradeOption}
            parts={parts}
            noContentWrapper
          />
        </div>
      </div>

      <div
        className="
          relative
          flex
          md:flex-col
          px-0
          pb-0
          box-border
          col-span-1
          order-1
          lg:col-span-7
          h-full
          row-span-full"
      >
        {/* TODO: Create a Dato type for the gradient, unsure how to do this right now */}
        <ColorSelectGallery
          images={images}
          productName={subscriptionProductName}
          gradient={subscriptionProduct.fromCms.gradient}
          showThumbnails
        />
        <div className="gap-2 sticky top-[9.94rem] flex-col sm:flex-row hidden lg:flex">
          <PartsPerformanceCard
            selectedConditionOption={selectedConditionOption}
            selectedUpgradeOption={selectedUpgradeOption}
            parts={parts}
            noContentWrapper
          />
        </div>
      </div>
      <BuySummary
        subscriptionProductName={subscriptionProductName}
        shortDescription={shortDescription}
        selectedUpgradeOption={selectedUpgradeOption}
        subscriptionProduct={subscriptionProduct}
        isRegionallyAvailable={isRegionallyAvailable}
        isVisible={isVisible}
        selectedConditionOption={selectedConditionOption}
        isOutOfStockConditionMap={isOutOfStockConditionMap}
      />
    </div>
  );
};

export default memo(SubscriptionPdp);
